export type PlantRole = 'thriller' | 'filler' | 'spiller'
export type SunExposure = 'full-sun' | 'partial-shade' | 'full-shade'
export type WaterNeed = 'low' | 'moderate' | 'high'
export type ContainerSize = 'small' | 'medium' | 'large' | 'xlarge'
export type ContainerShape = 'round' | 'rectangular' | 'square' | 'window-box'
export type LocationType = 'balcony' | 'patio' | 'windowsill' | 'indoors' | 'yard'
export type WindTolerance = 'low' | 'moderate' | 'high'
export type PestResistance = 'low' | 'moderate' | 'high'
export type CareLevel = 'easy' | 'moderate' | 'difficult'
export type PlantHeight = 'low' | 'medium' | 'tall'
export type RegionCode = 'OR' | 'WA'
export type GardeningGoal = 'food' | 'pollinators' | 'low-maintenance' | 'mixed'

export interface Plant {
  id: string
  name: string
  scientificName: string
  emoji: string
  accentColor: string
  role: PlantRole[]
  hardiness: { min: number; max: number }
  sunlight: SunExposure[]
  water: WaterNeed
  minContainerSize: 'small' | 'medium' | 'large'
  colors: string[]
  bloomMonths: number[]
  pestResistance: PestResistance
  nativeRegions: string[]
  toxicity: { pets: boolean; children: boolean }
  windTolerance: WindTolerance
  isPerennial: boolean
  overwinteringNote: string
  wateringFrequency: string
  fertilizingFrequency: string
  description: string
  careLevel: CareLevel
  height: PlantHeight
  tags: string[]
  reviewStatus?: 'verified' | 'partial'
  sourceNotes?: string | null
  ecologicalBenefits?: string[]
  crowdingGuidance?: string | null
  depthGuidance?: string | null
  containerSuitability?: 'container_friendly' | 'container_possible_with_care' | 'not_recommended'
}

export interface Container {
  id: string
  nickname: string
  size: ContainerSize
  shape: ContainerShape
  sunExposure: SunExposure
  location: LocationType
  plantIds: string[]
}

export interface GardenState {
  region: RegionCode | null
  preferredSunlight: SunExposure | null
  goal: GardeningGoal | null
  containers: Container[]
  savedPlantIds: string[]
}

export type GardenAction =
  | { type: 'SET_MVP_CONDITIONS'; region: RegionCode; sunlight: SunExposure; goal: GardeningGoal }
  | { type: 'ADD_CONTAINER'; container: Container }
  | { type: 'UPDATE_CONTAINER'; container: Container }
  | { type: 'REMOVE_CONTAINER'; id: string }
  | { type: 'ADD_PLANT_TO_CONTAINER'; containerId: string; plantId: string }
  | { type: 'REMOVE_PLANT_FROM_CONTAINER'; containerId: string; plantId: string }
  | { type: 'TOGGLE_SAVED_PLANT'; plantId: string }
  | { type: 'CLEAR_GARDEN' }

export const CONTAINER_SIZE_INFO: Record<ContainerSize, { label: string; inches: string; guidancePlants: number; depth: string }> = {
  small:      { label: 'Small Pot',    inches: '6–8"',   guidancePlants: 1, depth: '6–8"' },
  medium:     { label: 'Medium Pot',   inches: '10–14"', guidancePlants: 3, depth: '10–12"' },
  large:      { label: 'Large Pot',    inches: '16–20"', guidancePlants: 5, depth: '12–16"' },
  xlarge:     { label: 'Window Box / XL', inches: '24"+',  guidancePlants: 7, depth: '8–12"' },
}

export const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export const USDA_ZONES: Record<number, string> = {
  1: 'Zone 1 (below -50°F)',
  2: 'Zone 2 (-50 to -40°F)',
  3: 'Zone 3 (-40 to -30°F)',
  4: 'Zone 4 (-30 to -20°F)',
  5: 'Zone 5 (-20 to -10°F)',
  6: 'Zone 6 (-10 to 0°F)',
  7: 'Zone 7 (0 to 10°F)',
  8: 'Zone 8 (10 to 20°F)',
  9: 'Zone 9 (20 to 30°F)',
  10: 'Zone 10 (30 to 40°F)',
  11: 'Zone 11 (40 to 50°F)',
  12: 'Zone 12 (50 to 60°F)',
  13: 'Zone 13 (60 to 70°F)',
}
